// Gosto: o feed onde a pessoa julga saídas (curti / não curti, ou A × B) e o placar das versões. O agente que
// opera o Ateliê lê os votos e as notas (atelie taste export) e destila o gosto no prompt e nos critérios do juiz.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { ArrowLeft, Check, Heart, MessageSquare, PencilLine, Plus, RotateCcw, SkipForward, ThumbsDown, ThumbsUp, Trash2, X } from "lucide-react";
import { api, invalidate, timeAgo, toast, toastError, useQuery } from "../lib";
import { Badge, Button, Empty, Field, JsonView, Modal, Seg, cx } from "../ui";
import { Markdown } from "../markdown";

type Choice = "like" | "dislike" | "a" | "b" | "tie" | "neither";
interface Candidate { key: "a" | "b"; variant: string; label: string; status: string; output: any; format: string; error: string | null }
interface Item { id: string; idx: number; inputs: Record<string, any>; candidates: Candidate[]; ready: boolean; myVote: { choice: Choice; note: string | null; edit: string | null } | null }
interface VariantScore { variant: string; label: string; shown: number; likes: number; dislikes: number; wins: number; losses: number; ties: number; neither: number; score: number | null }
interface Results { voted: number; items: number; ready: number; raters: string[]; notes: number; variants: VariantScore[] }
interface Round {
  id: string; project: string; target: string | null; mode: "single" | "pair"; title: string; question: string; source: string;
  status: "generating" | "open" | "error"; total: number; done: number; error: string | null; createdAt: number; costUsd: number;
  items: Item[]; results: Results; mine: Results;
}

const pct = (x: number | null | undefined) => (x === null || x === undefined ? "–" : Math.round(x * 100) + "%");
const asText = (v: any) => (v === undefined || v === null ? "" : typeof v === "string" ? v : JSON.stringify(v, null, 2));
const isVideoUrl = (s: string) => /^(https?:\/\/|\/files\/)\S+\.(mp4|webm|mov)(\?\S*)?$/i.test(s);
const isImageUrl = (s: string) => /^(https?:\/\/|\/files\/)\S+\.(png|jpe?g|webp|gif)(\?\S*)?$/i.test(s) || s.startsWith("data:image/");

// ---------- saída: texto, markdown, JSON, imagem, vídeo ----------

function Media({ value, format }: { value: any; format: string }) {
  if (value === null || value === undefined) return <span className="faint">sem saída</span>;
  const urls: string[] = [];
  if (value && typeof value === "object") {
    for (const img of value.images ?? []) urls.push(typeof img === "string" ? img : img.url);
    for (const v of [].concat(value.video ?? [], value.videos ?? [])) urls.push(typeof v === "string" ? v : (v as any).url);
    if (typeof value.url === "string") urls.push(value.url);
  } else if (typeof value === "string" && (isImageUrl(value.trim()) || isVideoUrl(value.trim()))) urls.push(value.trim());
  if (urls.length || format === "image" || format === "video") {
    return (
      <div className="g-media">
        {urls.map((u) =>
          isVideoUrl(u) || format === "video" ? <video key={u} src={u} controls autoPlay loop muted playsInline /> : <img key={u} src={u} alt="" draggable={false} />
        )}
        {value?.text && <div className="text-out muted">{value.text}</div>}
      </div>
    );
  }
  if (typeof value === "object") return <JsonView value={value} />;
  const s = String(value);
  if (/(^|\n)(#{1,4} |[-*] |\d+\. |\|)|\*\*[^*]+\*\*/.test(s) && s.length > 280) return <Markdown text={s} />;
  return <div className="g-text">{s}</div>;
}

function Inputs({ inputs }: { inputs: Record<string, any> }) {
  const entries = Object.entries(inputs ?? {});
  const [open, setOpen] = useState(false);
  if (!entries.length) return null;
  const long = entries.some(([, v]) => asText(v).length > 360);
  return (
    <div className={cx("g-inputs", long && !open && "clamped")} onClick={() => long && setOpen(true)}>
      {entries.map(([k, v]) => (
        <div key={k} className="g-input">
          <div className="g-input-k">{k}</div>
          {typeof v === "string" && isImageUrl(v) ? <img src={v} alt="" className="g-input-img" /> : <div className="g-input-v">{asText(v)}</div>}
        </div>
      ))}
      {long && !open && <div className="g-more">ver tudo</div>}
    </div>
  );
}

// ---------- o feed ----------

export function TastePage({ p, id }: { p: string; id: string }) {
  const [, nav] = useLocation();
  const path = "/api/taste/" + id;
  const [poll, setPoll] = useState<number | undefined>(2000);
  const { data, error } = useQuery<Round>(path, { poll });
  useEffect(() => setPoll(data && data.status !== "generating" ? undefined : 2000), [data?.status]);
  const [local, setLocal] = useState<Record<string, Choice | null>>({});
  const [history, setHistory] = useState<string[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [leaving, setLeaving] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [edit, setEdit] = useState<string | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const shownAt = useRef(Date.now());

  const voted = (it: Item) => (it.id in local ? local[it.id] !== null : !!it.myVote);
  const queue = useMemo(() => {
    if (!data) return [];
    const open = data.items.filter((i) => i.ready && !voted(i));
    return [...open.filter((i) => !skipped.includes(i.id)), ...open.filter((i) => skipped.includes(i.id))];
  }, [data, local, skipped]);
  const current = queue[0] ?? null;
  const done = data ? data.items.filter(voted).length : 0;
  const pending = data ? data.items.filter((i) => !i.ready).length : 0;

  useEffect(() => {
    shownAt.current = Date.now();
    setNote("");
    setEdit(null);
    setNoteOpen(false);
  }, [current?.id]);

  const cast = useCallback(
    async (choice: Choice) => {
      if (!current || leaving) return;
      const it = current;
      const body = { item: it.id, choice, note: note.trim() || undefined, edit: edit?.trim() || undefined, ms: Date.now() - shownAt.current };
      setLeaving(choice);
      setTimeout(() => {
        setLocal((l) => ({ ...l, [it.id]: choice }));
        setHistory((h) => [...h, it.id]);
        setLeaving(null);
      }, 170);
      try {
        await api.post(path + "/votes", body);
        invalidate(path);
      } catch (e) {
        toastError(e);
        setLocal((l) => ({ ...l, [it.id]: null }));
      }
    },
    [current, leaving, note, edit]
  );

  const undo = useCallback(async () => {
    const last = history[history.length - 1];
    if (!last) return;
    setHistory((h) => h.slice(0, -1));
    setLocal((l) => ({ ...l, [last]: null }));
    setSkipped((s) => s.filter((x) => x !== last));
    try {
      await api.del(path + "/votes/" + last);
      invalidate(path);
    } catch (e) {
      toastError(e);
    }
  }, [history]);

  const skip = () => current && setSkipped((s) => [...s.filter((x) => x !== current.id), current.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement)?.closest?.("textarea, input");
      if (typing) {
        // Enter (sem Shift) ou Esc devolvem o teclado ao feed: a nota segue com o próximo voto.
        if (e.key === "Escape" || (e.key === "Enter" && !e.shiftKey)) {
          e.preventDefault();
          (e.target as HTMLElement).blur();
        }
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "z" || e.key === "Backspace") return void (e.preventDefault(), undo());
      if (!current || !data) return;
      const single = data.mode === "single";
      const map: Record<string, Choice | undefined> = single
        ? { ArrowLeft: "dislike", ArrowRight: "like", j: "dislike", l: "like" }
        : { ArrowLeft: "a", ArrowRight: "b", ArrowUp: "tie", ArrowDown: "neither", "1": "a", "2": "b" };
      const c = map[e.key];
      if (c) return void (e.preventDefault(), cast(c));
      if (e.key === "n") return void (e.preventDefault(), setNoteOpen(true));
      if (e.key === "e" && single && current.candidates[0]?.format !== "image") return void (e.preventDefault(), setNoteOpen(true), setEdit(asText(current.candidates[0].output)));
      if (e.key === "s") return void (e.preventDefault(), skip());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, data, cast, undo]);

  if (error) return <div className="g-screen"><div className="g-center"><Empty title="Não abriu">{error.message}</Empty></div></div>;
  if (!data) return <div className="g-screen"><div className="g-center"><span className="spinner" /></div></div>;
  const back = data.target ? "/p/" + p + "/t/" + data.target + "/gosto" : "/p/" + p;
  const finished = !current && !pending;

  return (
    <div className="g-screen">
      <header className="g-head">
        <Link href={back} className="btn btn-ghost btn-sm"><ArrowLeft size={14} /> Sair</Link>
        <div className="g-title">
          <div className="ellipsis">{data.title}</div>
          <div className="g-progress"><span style={{ width: (data.items.length ? (100 * done) / data.items.length : 0) + "%" }} /></div>
        </div>
        <span className="mono faint g-count">{done}/{data.items.length}</span>
        <Button size="sm" variant="ghost" icon={<RotateCcw size={13} />} disabled={!history.length} onClick={undo} title="Desfazer (Z)">Desfazer</Button>
        <Button size="sm" variant={showResults ? "primary" : undefined} onClick={() => setShowResults((v) => !v)}>Placar</Button>
      </header>

      {showResults || finished ? (
        <div className="g-scroll"><TasteResults round={data} p={p} onAgain={finished ? undefined : () => setShowResults(false)} /></div>
      ) : !current ? (
        <div className="g-center col" style={{ gap: 12 }}>
          <span className="spinner" />
          <span className="faint">gerando as saídas · {data.done}/{data.total}</span>
        </div>
      ) : (
        <div className="g-scroll">
          <div className={cx("g-stage", data.mode === "pair" && "g-stage-pair")}>
            <div className="g-question">{data.question}</div>
            <Inputs inputs={current.inputs} />
            {data.mode === "single" ? (
              <SwipeCard key={current.id} c={current.candidates[0]} leaving={leaving} onVote={cast} />
            ) : (
              <div className={cx("g-pair", leaving && "leaving")}>
                {current.candidates.map((c) => (
                  <button key={c.key} className={cx("g-card g-choice", leaving === c.key && "picked", leaving && leaving !== c.key && "dropped", (leaving === "tie" && "picked") || (leaving === "neither" && "dropped"))} onClick={() => cast(c.key)}>
                    <div className="g-card-tag"><span className="kbd">{c.key === "a" ? "←" : "→"}</span> {c.key.toUpperCase()}</div>
                    {c.status === "error" ? <div className="g-error">{c.error}</div> : <Media value={c.output} format={c.format} />}
                  </button>
                ))}
              </div>
            )}
            <div className="g-actions">
              {data.mode === "single" ? (
                <>
                  <button className="g-big g-no" onClick={() => cast("dislike")} title="Não curti (←)"><X size={26} /></button>
                  <button className="g-small" onClick={() => setNoteOpen((v) => !v)} title="Nota (N)"><MessageSquare size={16} /></button>
                  <button className="g-small" onClick={skip} title="Pular (S)"><SkipForward size={16} /></button>
                  <button className="g-big g-yes" onClick={() => cast("like")} title="Curti (→)"><Heart size={24} /></button>
                </>
              ) : (
                <>
                  <Button size="sm" onClick={() => cast("neither")}><span className="kbd">↓</span> Nenhuma</Button>
                  <Button size="sm" onClick={() => cast("tie")}><span className="kbd">↑</span> As duas boas</Button>
                  <Button size="sm" variant="ghost" icon={<MessageSquare size={13} />} onClick={() => setNoteOpen((v) => !v)}>Nota</Button>
                  <Button size="sm" variant="ghost" icon={<SkipForward size={13} />} onClick={skip}>Pular</Button>
                </>
              )}
            </div>
            {noteOpen && (
              <div className="g-note">
                <textarea
                  className="textarea"
                  autoFocus
                  rows={2}
                  placeholder={data.mode === "pair" ? "Por quê? (vai com o próximo voto)" : "Por quê? (vai com o voto)"}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                {data.mode === "single" && edit !== null && (
                  <textarea className="textarea" rows={3} placeholder="Como você escreveria" value={edit} onChange={(e) => setEdit(e.target.value)} />
                )}
                {data.mode === "single" && edit === null && current.candidates[0]?.format !== "image" && (
                  <button className="g-link" onClick={() => setEdit(asText(current.candidates[0].output))}><PencilLine size={12} /> reescrever do meu jeito</button>
                )}
              </div>
            )}
            <div className="g-keys faint">
              {data.mode === "single" ? "← não curti · → curti · arraste o cartão · N nota · E reescrever · S pular · Z desfazer" : "← A · → B · ↑ as duas boas · ↓ nenhuma · N nota · S pular · Z desfazer"}
              {pending ? " · " + pending + " ainda gerando" : ""}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Cartão do modo single: arrastar para os lados vota (como no Tinder).
function SwipeCard({ c, leaving, onVote }: { c: Candidate; leaving: string | null; onVote: (c: Choice) => void }) {
  const [dx, setDx] = useState(0);
  const start = useRef<number | null>(null);
  const moved = useRef(false);
  const out = leaving === "like" ? 1 : leaving === "dislike" ? -1 : 0;
  const x = out ? out * 520 : dx;
  const hint = x > 50 ? "like" : x < -50 ? "dislike" : null;
  return (
    <div
      className={cx("g-card g-swipe", start.current !== null && "dragging")}
      style={{ transform: "translateX(" + x + "px) rotate(" + x / 28 + "deg)", opacity: out ? 0 : 1 }}
      onPointerDown={(e) => {
        if ((e.target as HTMLElement).closest("video, a")) return;
        start.current = e.clientX;
        moved.current = false;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (start.current === null) return;
        const d = e.clientX - start.current;
        if (Math.abs(d) > 4) moved.current = true;
        setDx(d);
      }}
      onPointerUp={() => {
        if (start.current === null) return;
        start.current = null;
        if (dx > 110) onVote("like");
        else if (dx < -110) onVote("dislike");
        setDx(0);
      }}
      onPointerCancel={() => ((start.current = null), setDx(0))}
    >
      <div className={cx("g-stamp g-stamp-yes", hint === "like" && "on")}>CURTI</div>
      <div className={cx("g-stamp g-stamp-no", hint === "dislike" && "on")}>NÃO</div>
      {c.status === "error" ? <div className="g-error">{c.error}</div> : <Media value={c.output} format={c.format} />}
    </div>
  );
}

// ---------- placar ----------

export function TasteResults({ round, p, onAgain }: { round: Round; p: string; onAgain?: () => void }) {
  const r = round.results;
  const pair = round.mode === "pair";
  const decisive = pair ? (r.variants[0]?.wins ?? 0) + (r.variants[0]?.losses ?? 0) : r.voted;
  const notes = round.items.filter((i) => i.myVote?.note || i.myVote?.edit);
  return (
    <div className="g-results">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div className="page-title">{r.voted === r.items ? "Rodada completa" : "Placar parcial"}</div>
          <div className="page-sub">
            {r.voted} de {r.items} itens votados{r.raters.length ? " por " + r.raters.join(", ") : ""}. {pair ? "Vitórias contam só as escolhas entre as duas; empates e “nenhuma” aparecem à parte." : "Taxa de curtidas por versão."}
          </div>
        </div>
        {onAgain && <Button onClick={onAgain}>Continuar votando</Button>}
      </div>
      <div className="card">
        {r.variants.map((v) => (
          <div key={v.variant} className="g-score">
            <div className="g-score-name">
              <span>{v.label}</span>
              <span className="mono faint" style={{ fontSize: 11 }}>{v.variant.split("|")[1] ?? ""}</span>
            </div>
            <div className="g-bar"><span style={{ width: (v.score ?? 0) * 100 + "%" }} /></div>
            <div className="g-score-pct mono">{pct(v.score)}</div>
            <div className="g-score-detail faint mono">
              {pair ? v.wins + " ganhou · " + v.losses + " perdeu · " + v.ties + " as duas · " + v.neither + " nenhuma" : v.likes + " curti · " + v.dislikes + " não"}
            </div>
          </div>
        ))}
      </div>
      {decisive > 0 && decisive < 10 && <div className="faint" style={{ fontSize: 12.5 }}>Poucos votos decididos ({decisive}): leia como tendência.</div>}
      {notes.length > 0 && (
        <div className="card">
          <div className="card-head"><span className="card-title">Suas notas</span><span className="faint">{notes.length}</span></div>
          <div className="card-body col" style={{ gap: 14 }}>
            {notes.map((i) => {
              const v = i.myVote!;
              const chosen = pair ? i.candidates.find((c) => c.key === v.choice) : i.candidates[0];
              return (
                <div key={i.id} className="col" style={{ gap: 6 }}>
                  <div className="row wrap" style={{ gap: 6 }}>
                    <Badge tone={v.choice === "dislike" || v.choice === "neither" ? "red" : "green"}>{CHOICE_LABEL[v.choice]}</Badge>
                    {chosen && pair && <span className="chip">{chosen.label}</span>}
                  </div>
                  {v.note && <div className="fb-note">{v.note}</div>}
                  {v.edit && <div className="fb-note mono" style={{ fontSize: 12.5 }}>reescrita: {v.edit}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="card card-note" style={{ borderBottom: 0 }}>
        O agente lê isto com <span className="mono">atelie taste export {round.id}</span> e mede o juiz contra os seus votos com <span className="mono">atelie taste agree {round.id}</span>.
      </div>
    </div>
  );
}

const CHOICE_LABEL: Record<Choice, string> = { like: "curti", dislike: "não curti", a: "escolheu A", b: "escolheu B", tie: "as duas boas", neither: "nenhuma" };

// ---------- aba do alvo ----------

export function TasteTab({ p, t, data }: { p: string; t: string; data: any }) {
  const path = "/api/projects/" + p + "/taste?target=" + t;
  const { data: rounds } = useQuery<any[]>(path, { poll: 5000 });
  const [creating, setCreating] = useState(false);
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Gosto</h1>
          <div className="page-sub">
            Você vota num feed (curti / não curti, ou qual das duas) e o agente destila o seu gosto no prompt e nos critérios do juiz. As saídas vêm dos casos de teste, da produção ou de fora (imagens e vídeos).
          </div>
        </div>
        <Button variant="primary" icon={<Plus size={14} />} onClick={() => setCreating(true)}>Nova rodada</Button>
      </div>
      {rounds && !rounds.length && (
        <div className="card">
          <Empty icon={<Heart size={18} />} title="Nenhuma rodada ainda">
            Crie uma aqui ou peça ao agente: <span className="mono">atelie taste new {p}/{t} --versions production,draft</span>
          </Empty>
        </div>
      )}
      <div className="col" style={{ gap: 10 }}>
        {rounds?.map((r) => {
          const res = r.results;
          const complete = res.items && res.voted === res.items;
          return (
            <Link key={r.id} href={"/p/" + p + "/g/" + r.id} className="card g-round">
              <div className="col grow" style={{ gap: 4 }}>
                <div className="row" style={{ gap: 8 }}>
                  <span className="card-title">{r.title}</span>
                  <Badge tone={r.mode === "pair" ? "violet" : "blue"}>{r.mode === "pair" ? "A × B" : "curti / não"}</Badge>
                  {r.source === "prod" && <Badge tone="green">produção</Badge>}
                  {r.status === "generating" && <Badge tone="amber">gerando {r.done}/{r.total}</Badge>}
                </div>
                <div className="faint" style={{ fontSize: 12 }}>
                  {timeAgo(r.createdAt)} · {res.voted}/{res.items} votados{res.notes ? " · " + res.notes + " notas" : ""}
                </div>
              </div>
              <div className="g-round-scores">
                {res.voted ? res.variants.filter((v: VariantScore) => v.score !== null).slice(0, 3).map((v: VariantScore) => (
                  <div key={v.variant} className="row" style={{ gap: 8 }}>
                    <span className="ellipsis" style={{ width: 120, textAlign: "right", fontSize: 12 }}>{v.label}</span>
                    <div className="g-bar g-bar-sm"><span style={{ width: (v.score ?? 0) * 100 + "%" }} /></div>
                    <span className="mono" style={{ width: 36, fontSize: 12 }}>{pct(v.score)}</span>
                  </div>
                )) : <span className="faint" style={{ fontSize: 12 }}>sem votos</span>}
              </div>
              <span className={cx("btn btn-sm", !complete && "btn-primary")}>{complete ? "Ver placar" : "Votar"}</span>
            </Link>
          );
        })}
      </div>
      {creating && <NewRoundModal p={p} t={t} data={data} onClose={() => setCreating(false)} />}
    </div>
  );
}

function NewRoundModal({ p, t, data, onClose }: { p: string; t: string; data: any; onClose: () => void }) {
  const [, nav] = useLocation();
  const channels: Record<string, number> = data?.meta?.channels ?? {};
  const refs = [
    { value: "draft", label: "rascunho" },
    ...Object.keys(channels).map((c) => ({ value: c, label: c + " (v" + channels[c] + ")" })),
    ...(data?.versions ?? []).map((v: any) => ({ value: "v" + v.n, label: "v" + v.n + (v.message ? " · " + v.message : "") })),
  ];
  const [source, setSource] = useState<"cases" | "prod">("cases");
  const [mode, setMode] = useState<"pair" | "single">("pair");
  const [a, setA] = useState(channels.production ? "production" : refs[refs.length - 1]?.value ?? "draft");
  const [b, setB] = useState("draft");
  const [limit, setLimit] = useState(20);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body: any = { target: t, source, limit, question: question.trim() || undefined };
      if (source === "cases") Object.assign(body, { mode, variants: mode === "pair" ? [{ ref: a }, { ref: b }] : [{ ref: b }] });
      else if (mode === "pair") body.variants = [{ ref: b }];
      const r = await api.post("/api/projects/" + p + "/taste", body);
      invalidate("/api/projects/" + p + "/taste");
      nav("/p/" + p + "/g/" + r.id);
    } catch (e) {
      toastError(e);
      setBusy(false);
    }
  };
  const sel = (v: string, set: (v: string) => void) => (
    <select className="select" value={v} onChange={(e) => set(e.target.value)}>
      {refs.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
    </select>
  );
  return (
    <Modal open onClose={onClose} width={560} title="Nova rodada de gosto" footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button variant="primary" loading={busy} onClick={submit}>Criar e votar</Button></>}>
      <Field label="De onde vêm as saídas">
        <Seg value={source} onChange={setSource} options={[{ value: "cases", label: "Casos de teste" }, { value: "prod", label: "Produção" }]} />
      </Field>
      <Field label="Como votar">
        <Seg value={mode} onChange={setMode} options={[{ value: "pair", label: "A × B" }, { value: "single", label: "Curti / não curti" }]} />
      </Field>
      {source === "cases" && mode === "pair" && (
        <div className="row" style={{ gap: 10 }}>
          <Field label="Versão A">{sel(a, setA)}</Field>
          <Field label="Versão B">{sel(b, setB)}</Field>
        </div>
      )}
      {((source === "cases" && mode === "single") || (source === "prod" && mode === "pair")) && (
        <Field label={source === "prod" ? "Desafiante (contra o que está no ar)" : "Versão"}>{sel(b, setB)}</Field>
      )}
      <div className="row" style={{ gap: 10 }}>
        <Field label="Itens"><input className="input" type="number" min={1} max={200} value={limit} onChange={(e) => setLimit(Number(e.target.value) || 20)} /></Field>
        <Field label="Pergunta" hint="Aparece no topo do feed"><input className="input" placeholder={mode === "pair" ? "Qual das duas você prefere?" : "Você curtiu?"} value={question} onChange={(e) => setQuestion(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

